import { useEffect, useState } from "react";
import Seo from "@/components/base/Seo";
import EmptyState from "@/components/base/EmptyState";
import AdminPageHeader from "../components/AdminPageHeader";
import { useAuth } from "@/context/AuthContext";
import { getPendingApprovals, getApprovalAuditLog, adminAction } from "@/services/adminApi";
import type { ApprovalAuditEntry } from "@/types";

type Approval = Awaited<ReturnType<typeof getPendingApprovals>>[number];

function decisionStamp(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

export default function AdminApprovals() {
  const { token, user } = useAuth();
  const [items, setItems] = useState<Approval[]>([]);
  const [auditLog, setAuditLog] = useState<ApprovalAuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [notice, setNotice] = useState("");
  const [actionError, setActionError] = useState("");
  const [busyId, setBusyId] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    Promise.all([getPendingApprovals(token), getApprovalAuditLog(token)])
      .then(([pending, log]) => {
        if (!active) return;
        setItems(pending);
        setAuditLog(log);
      })
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token]);

  const decide = async (id: string, approved: boolean, note: string) => {
    const item = items.find((i) => i.id === id);
    if (!item || busyId) return;
    setBusyId(id);

    // Persist the decision to the real review audit log on the backend.
    const ok = await adminAction(
      `/admin/approvals/${id}/decision`,
      { decision: approved ? "approved" : "rejected", note },
      token,
    );
    if (!ok) {
      setBusyId("");
      setActionError("Couldn't save that decision. Please try again.");
      setTimeout(() => setActionError(""), 3200);
      return;
    }

    const entry: ApprovalAuditEntry = {
      id: `audit-${Date.now()}`,
      businessId: id,
      businessName: item.name,
      reviewer: user?.name ?? "Admin Console",
      decision: approved ? "approved" : "rejected",
      note,
      decidedAt: decisionStamp(),
    };

    setItems((prev) => prev.filter((i) => i.id !== id));
    setAuditLog((prev) => [entry, ...prev].slice(0, 40));
    setBusyId("");
    setNotice(`${item.name} ${approved ? "approved and published" : "rejected and archived"}.`);
    setTimeout(() => setNotice(""), 2600);
  };

  return (
    <>
      <Seo title="Pending Approvals" path="/admin/approvals" />

      <AdminPageHeader
        icon="ri-checkbox-circle-line"
        title="Pending approvals"
        subtitle="Review newly submitted listings — every decision is written to the audit log."
        actions={
          actionError ? (
            <span className="inline-flex items-center gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-4 py-2 text-sm text-secondary-900">
              <i className="ri-error-warning-line text-secondary-600"></i> {actionError}
            </span>
          ) : notice ? (
            <span className="inline-flex items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-2 text-sm text-accent-900">
              <i className="ri-checkbox-circle-line text-accent-600"></i> {notice}
            </span>
          ) : undefined
        }
      />

      <div className="mt-6 grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-5">
        <div>
          {loading ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-40 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
              ))}
            </div>
          ) : error ? (
            <EmptyState icon="ri-wifi-off-line" title="Couldn't load approvals" description="Please refresh and try again." />
          ) : items.length === 0 ? (
            <EmptyState
              icon="ri-checkbox-circle-line"
              title="All caught up"
              description="There are no listings waiting for review right now."
            />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {items.map((item) => (
                <article key={item.id} className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h2 className="truncate font-heading text-base font-bold text-foreground-950">{item.name}</h2>
                      <p className="mt-1 text-xs text-foreground-500">
                        {item.categoryName} · {item.locationName}
                      </p>
                    </div>
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary-100 px-2.5 py-1 text-[11px] font-bold text-primary-900">
                      <i className="ri-time-line"></i> Pending
                    </span>
                  </div>

                  <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <dt className="text-[11px] font-semibold uppercase tracking-wide text-foreground-500">Owner</dt>
                      <dd className="mt-0.5 text-foreground-800">{item.owner}</dd>
                    </div>
                    <div>
                      <dt className="text-[11px] font-semibold uppercase tracking-wide text-foreground-500">Phone</dt>
                      <dd className="mt-0.5 text-foreground-800">{item.phone}</dd>
                    </div>
                    <div>
                      <dt className="text-[11px] font-semibold uppercase tracking-wide text-foreground-500">Submitted</dt>
                      <dd className="mt-0.5 text-foreground-800">{item.submittedAt}</dd>
                    </div>
                    <div>
                      <dt className="text-[11px] font-semibold uppercase tracking-wide text-foreground-500">Slug</dt>
                      <dd className="mt-0.5 truncate text-foreground-800">/{item.slug}</dd>
                    </div>
                  </dl>

                  <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-background-100 pt-4">
                    <button
                      type="button"
                      onClick={() => decide(item.id, true, "Approved after manual review.")}
                      disabled={busyId === item.id}
                      className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-accent-600 disabled:opacity-50 whitespace-nowrap cursor-pointer"
                    >
                      <i className={busyId === item.id ? "ri-loader-4-line animate-spin" : "ri-check-line"}></i> Approve
                    </button>
                    <button
                      type="button"
                      onClick={() => decide(item.id, false, "Rejected — resubmission requested.")}
                      disabled={busyId === item.id}
                      className="inline-flex items-center gap-2 rounded-md border border-secondary-300 px-4 py-2 text-sm font-semibold text-secondary-800 hover:bg-secondary-50 disabled:opacity-50 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-close-line"></i> Reject
                    </button>
                    <a
                      href={`/business/${item.slug}`}
                      className="ml-auto inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 hover:text-primary-800 whitespace-nowrap cursor-pointer"
                    >
                      Preview <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>

        <aside className="rounded-lg border border-background-200 bg-background-50">
          <div className="flex items-center justify-between gap-2 border-b border-background-100 px-5 py-4">
            <h2 className="font-heading text-sm font-bold text-foreground-950">Review audit log</h2>
            <span className="inline-flex items-center gap-1 rounded-full bg-background-100 px-2.5 py-1 text-[11px] font-semibold text-foreground-600">
              <i className="ri-history-line"></i> {auditLog.length}
            </span>
          </div>
          {auditLog.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-foreground-500">
              No decisions recorded yet. Approvals and rejections will appear here.
            </p>
          ) : (
            <ul className="max-h-[640px] overflow-y-auto px-5 py-2">
              {auditLog.map((entry) => {
                const approved = entry.decision === "approved";
                return (
                  <li key={entry.id} className="flex gap-3 border-b border-background-100 py-3 last:border-b-0">
                    <span
                      className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
                        approved ? "bg-accent-100 text-accent-800" : "bg-secondary-100 text-secondary-800"
                      }`}
                    >
                      <i className={approved ? "ri-check-line" : "ri-close-line"}></i>
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground-950">
                        {entry.businessName}{" "}
                        <span className={approved ? "text-accent-700" : "text-secondary-700"}>
                          {entry.decision}
                        </span>
                      </p>
                      <p className="mt-0.5 text-xs text-foreground-600">{entry.note}</p>
                      <p className="mt-1 text-[11px] text-foreground-400">
                        {entry.reviewer} · {entry.decidedAt}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>
      </div>
    </>
  );
}